import * as React from 'react';
import React__default, { useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { Flyout, Link, LazyImage, MediaQuery, Logo, Accordion, AccordionItem, RichTextArea, Button, Modal, ReorderButton, CartButton, Util, Backdrop } from '@grandvisionhq/common';
import * as swcHelpers from '@swc/helpers';
import { Newsletter, CategoryLinks, CategoryLinksItem } from '@grandvisionhq/content';
import { Heading, Container } from '@grandvisionhq/elements';
import { parseHtml, LocaleUtils, useEventListener } from '@grandvisionhq/utils';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { Icon } from '@grandvisionhq/icons';
import { SearchBar } from '@grandvisionhq/search';
import FocusTrap from 'focus-trap-react';
import { motion } from 'framer-motion';

const getCurrentItem = (options)=>{
    return options.find((option)=>option.url === '/'
    );
};
const createSubMenu$1 = (options)=>{
    return options.filter((option)=>option.url !== '/'
    ).map((option)=>/*#__PURE__*/ React.createElement(Flyout.MenuItem, {
            key: option.label,
            link: option.url
        }, option.image ? /*#__PURE__*/ React.createElement("img", {
            src: option.image,
            alt: option.label,
            className: "country-switcher-menu__image"
        }) : option.label)
    );
};
const CountrySwitcherMenu = ({ options , className , tabIndex , ariaHidden =false ,  })=>{
    const currentItem = getCurrentItem(options);
    const subMenuItems = createSubMenu$1(options);
    if (!currentItem || subMenuItems.length === 0) {
        return null;
    }
    const triggerElement = currentItem.image ? /*#__PURE__*/ React.createElement("img", {
        className: "country-switcher-menu__image",
        src: currentItem.image,
        alt: currentItem.label
    }) : currentItem.label;
    return /*#__PURE__*/ React.createElement(Flyout, {
        "data-t": "country-switcher-menu",
        className: classnames('country-switcher-menu', className)
    }, /*#__PURE__*/ React.createElement(Flyout.Trigger, {
        className: "header-menu__icon country-switcher-menu__trigger",
        title: currentItem.label,
        tabIndex: tabIndex,
        "aria-hidden": ariaHidden
    }, triggerElement), /*#__PURE__*/ React.createElement(Flyout.Menu, {
        className: "country-switcher-menu__flyout-menu"
    }, subMenuItems));
};

const FooterAppLinks = ({ items  })=>{
    if (!items.length) {
        return null;
    }
    var _description;
    return /*#__PURE__*/ React.createElement("div", {
        className: "footer__apps"
    }, items.map((item, index)=>item.image && /*#__PURE__*/ React.createElement("div", {
            key: index,
            className: "footer__apps-item",
            "aria-label": item.link.label
        }, /*#__PURE__*/ React.createElement(Link, {
            className: "footer__apps-link",
            url: item.link.url,
            target: item.link.openInNewWindow ? 'external' : undefined
        }, /*#__PURE__*/ React.createElement(LazyImage, {
            className: "footer__apps-image",
            src: item.image.url,
            alt: (_description = item.image.description) !== null && _description !== void 0 ? _description : '',
            height: 40,
            width: 120
        })))
    ));
};

const FooterPaymentOptions = ({ items  })=>{
    if (!items.length) {
        return null;
    }
    var _description;
    return /*#__PURE__*/ React.createElement("div", {
        className: "footer__payment"
    }, /*#__PURE__*/ React.createElement("ul", {
        className: "footer__payment-items"
    }, items.map((item, index)=>/*#__PURE__*/ React.createElement("li", {
            key: `payment-item-${index}`,
            className: "footer__payment-item"
        }, /*#__PURE__*/ React.createElement(LazyImage, {
            src: item.url,
            alt: (_description = item.description) !== null && _description !== void 0 ? _description : '',
            width: 48,
            height: 30
        }))
    )));
};

const FooterSocialLinks = ({ title , items  })=>{
    if (!items.length) {
        return null;
    }
    var _description, _description1;
    return /*#__PURE__*/ React.createElement("section", {
        className: "footer__section footer__social"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "div",
        className: "footer-section__title"
    }, title), /*#__PURE__*/ React.createElement("ul", {
        className: "footer__social-links"
    }, items.map((item, index)=>item.link && item.image && /*#__PURE__*/ React.createElement("li", {
            key: `footer-social-link-${index}`,
            className: "footer__social-links-item",
            "aria-label": (_description = item.image.description) !== null && _description !== void 0 ? _description : ''
        }, /*#__PURE__*/ React.createElement(Link, {
            className: "footer__social-link",
            url: item.link.url,
            target: item.link.openInNewWindow ? 'external' : undefined
        }, /*#__PURE__*/ React.createElement(LazyImage, {
            className: "footer__social-image",
            src: item.image.url,
            alt: (_description1 = item.image.description) !== null && _description1 !== void 0 ? _description1 : '',
            height: 32,
            width: 32
        })))
    )));
};

const Footer = ({ appLinks , additionalContent , paymentOptionLogos , socialMediaLinks , content: contentSections , socialTitle , title , logo , disclaimer , siteName , newsletterProps , children ,  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const footerSections = React.useMemo(()=>{
        return (contentSections === null || contentSections === void 0 ? void 0 : contentSections.map((section, index)=>({
                id: section.id || `footer-section-${index}`,
                title: section.title,
                body: section.body.value
            })
        )) || [];
    }, [
        contentSections
    ]);
    const logoTitle = siteName ? `${siteName} home` : 'Home';
    if (!contentSections) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("footer", {
        className: "footer",
        role: "contentinfo"
    }, /*#__PURE__*/ React.createElement(Container, null, (logo === null || logo === void 0 ? void 0 : logo.url) && /*#__PURE__*/ React.createElement("div", {
        className: "footer__top"
    }, /*#__PURE__*/ React.createElement(Logo, {
        className: "footer__logo",
        url: "/",
        image: logo,
        title: logoTitle,
        srOnlyText: siteName
    })), title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        component: "div",
        className: "footer__title"
    }, title), /*#__PURE__*/ React.createElement("div", {
        className: "footer__content"
    }, isSmallScreen ? /*#__PURE__*/ React.createElement(Accordion, {
        className: "footer__accordion"
    }, footerSections.map((item)=>/*#__PURE__*/ React.createElement(AccordionItem, {
            key: item.id,
            id: item.id,
            label: item.title,
            headingLevel: "div",
            fullWidth: true
        }, /*#__PURE__*/ React.createElement(RichTextArea, {
            className: "footer__rich-text",
            content: item.body
        }))
    )) : /*#__PURE__*/ React.createElement("div", {
        className: "footer__sections"
    }, footerSections.map((footerSection)=>/*#__PURE__*/ React.createElement("section", {
            className: "footer-section",
            key: footerSection.id
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "smallest",
            component: "div",
            className: "footer-section__title"
        }, parseHtml(footerSection.title)), /*#__PURE__*/ React.createElement("div", {
            className: "footer-section__content"
        }, /*#__PURE__*/ React.createElement(RichTextArea, {
            className: "footer__rich-text",
            content: footerSection.body
        })))
    )), /*#__PURE__*/ React.createElement("section", {
        className: "footer__aside"
    }, /*#__PURE__*/ React.createElement(FooterSocialLinks, {
        title: socialTitle,
        items: socialMediaLinks
    }), appLinks && /*#__PURE__*/ React.createElement(FooterAppLinks, {
        items: appLinks
    }), newsletterProps && /*#__PURE__*/ React.createElement(Newsletter, swcHelpers.extends({}, newsletterProps, {
        variant: "modal"
    })), additionalContent && /*#__PURE__*/ React.createElement("div", {
        className: "footer__additional-content"
    }, parseHtml(additionalContent.html)))), paymentOptionLogos && /*#__PURE__*/ React.createElement(FooterPaymentOptions, {
        items: paymentOptionLogos
    }), children, disclaimer && /*#__PURE__*/ React.createElement("div", {
        className: "footer__legal"
    }, parseHtml(disclaimer.html))));
};

const FooterLinks = ({ items  })=>{
    if (!items.length) {
        return null;
    }
    var ref;
    return /*#__PURE__*/ React.createElement("ul", {
        className: "footer__links"
    }, items.map((item, index)=>{
        var ref1;
         return React.createElement("li", {
            key: `footer-link-${index}`,
            className: "footer__links-item"
        }, /*#__PURE__*/ React.createElement(Link, {
            className: "footer__link",
            url: (ref = (ref1 = item.link) === null || ref1 === void 0 ? void 0 : ref1.href) !== null && ref !== void 0 ? ref : '#',
            label: item.label
        }));
    }));
};

const BrokenGlasses = ({ ariaLabel  })=>/*#__PURE__*/ React.createElement("svg", {
        width: "388",
        height: "130",
        className: "icon icon--broken-glasses",
        "aria-label": ariaLabel
    }, /*#__PURE__*/ React.createElement("path", {
        d: "M387.366 12.9C387.065 8.66826 386.16 7.65263 381.678 7.31409C375.903 6.84859 370.085 6.637 364.439 6.08687C356.122 5.24051 347.891 4.01328 339.573 3.25156L329.36 2.36289L311.432 0.924083L302.511 0.204687C295.227 -0.190854 287.922 0.135228 280.705 1.178C275.878 1.72813 271.008 2.10899 266.225 2.78608C259.588 3.71707 252.865 4.90197 246.401 6.25614C238.299 7.94886 230.24 9.76852 222.224 11.6728C212.956 13.6741 203.485 14.6254 193.996 14.5081C184.621 14.5901 175.267 13.6247 166.114 11.6305C158.055 9.76851 149.996 7.94885 141.937 6.21382C135.473 4.85964 128.75 3.67474 122.113 2.74375C117.286 2.06667 112.46 1.6858 107.633 1.13567C100.416 0.0934729 93.1101 -0.232606 85.8265 0.16236L76.9488 0.881772L58.9779 2.32058L48.7643 3.20925C40.4468 3.97097 32.2156 5.19818 23.8981 6.04454C18.1233 6.63699 12.3054 6.84858 6.65992 7.27176C2.35036 7.6103 1.27297 8.5413 0.971304 12.8577C0.971304 13.958 0.755826 15.0583 0.712731 16.2008C0.0662973 20.9404 0.0662969 25.68 0.0662969 30.3773C-0.112878 31.3327 0.0740928 32.3193 0.591232 33.1474C1.10837 33.9756 1.91914 34.5866 2.86751 34.863C5.44056 35.5224 7.74395 36.9449 9.46373 38.9365C11.1835 40.9282 12.236 43.3922 12.4778 45.9926C13.5983 52.8058 14.9343 59.5343 16.1409 66.3475C17.8293 76.9699 20.7203 87.3741 24.7601 97.3665C27.7976 106.188 33.9002 113.679 41.9983 118.525C47.6559 121.611 53.6197 124.121 59.7968 126.016C70.1559 129.141 80.9958 130.445 91.8168 129.867C122.285 128.174 149.867 114.124 164.174 86.9563C169.759 75.9242 174.526 64.5111 178.439 52.8058C179.507 49.563 181.595 46.736 184.402 44.7309C187.208 42.7258 190.59 41.6457 194.061 41.6457C197.532 41.6457 200.914 42.7258 203.72 44.7309C206.527 46.736 208.615 49.563 209.683 52.8058C213.592 64.5127 218.359 75.9261 223.948 86.9563C238.212 114.124 265.837 128.174 296.305 129.867C307.126 130.443 317.966 129.139 328.325 126.016C334.502 124.119 340.466 121.61 346.124 118.525C354.219 113.676 360.321 106.187 363.362 97.3665C367.406 87.3755 370.297 76.9709 371.981 66.3475C373.188 59.5766 374.524 52.8058 375.644 45.9926C375.886 43.3922 376.939 40.9282 378.658 38.9365C380.378 36.9449 382.681 35.5224 385.255 34.863C386.179 34.5618 386.96 33.9403 387.452 33.1148C387.943 32.2892 388.112 31.3161 387.926 30.3773C387.926 25.6377 387.926 20.9404 387.625 16.2431C387.582 15.1006 387.409 14.0849 387.366 12.9ZM162.235 67.9133C160.076 76.316 156.664 84.3588 152.107 91.7805C146.169 100.732 138.198 108.21 128.822 113.629C119.446 119.047 108.918 122.259 98.0656 123.011C90.4435 123.781 82.761 123.781 75.1388 123.011C69.9106 122.582 64.7877 121.325 59.9691 119.287C58.0298 118.398 55.9613 117.679 54.022 116.833C52.0827 115.986 50.7467 115.098 49.1091 114.294C44.5623 112.026 40.5844 108.797 37.462 104.838C34.3395 100.879 32.1503 96.2895 31.052 91.3997C26.2157 73.9477 24.826 55.748 26.9579 37.7829C27.539 34.1616 28.4772 30.6043 29.7591 27.1612C30.8694 24.4446 32.5829 22.0055 34.7748 20.0215C36.9667 18.0376 39.5817 16.5589 42.4292 15.693C50.9382 12.3171 59.8898 10.1373 69.0192 9.21838C82.8073 7.4233 96.7571 7.15361 110.606 8.41435C123.992 9.37124 137.111 12.5775 149.392 17.8936C151.898 19.0233 154.256 20.444 156.417 22.1253C157.401 22.8247 158.323 23.6032 159.175 24.4528C160.075 25.6173 160.882 26.8482 161.589 28.1345C162.364 29.3194 163.226 30.4196 163.959 31.6469C165.185 34.3324 165.984 37.1868 166.329 40.1104C166.461 49.5595 165.079 58.9701 162.235 67.9979V67.9133ZM356.898 91.442C355.799 96.3318 353.61 100.922 350.488 104.88C347.365 108.839 343.387 112.069 338.841 114.336C337.203 115.14 335.608 116.198 333.928 116.875C332.247 117.552 329.92 118.441 327.98 119.329C323.163 121.37 318.04 122.628 312.811 123.053C305.189 123.823 297.506 123.823 289.884 123.053C279.031 122.301 268.504 119.09 259.128 113.671C249.751 108.252 241.781 100.774 235.842 91.8228C231.303 84.3978 227.905 76.3553 225.758 67.9556C222.97 58.9045 221.647 49.4799 221.836 40.0258C222.178 37.1017 222.978 34.2469 224.206 31.5622C224.939 30.335 225.801 29.2347 226.577 28.0498C227.284 26.7635 228.09 25.5327 228.99 24.3682C229.843 23.5198 230.766 22.7414 231.748 22.0407C233.909 20.3594 236.267 18.9386 238.773 17.8089C251.053 12.4921 264.173 9.28577 277.559 8.32971C291.408 7.06825 305.358 7.33797 319.146 9.13376C328.248 10.0752 337.17 12.269 345.65 15.6507C348.496 16.5192 351.11 17.999 353.301 19.9826C355.492 21.9662 357.207 24.4039 358.32 27.1188C359.595 30.5641 360.533 34.1207 361.121 37.7406C363.227 55.7415 361.794 73.9721 356.898 91.442Z"
    }), /*#__PURE__*/ React.createElement("path", {
        d: "M101.351 52.9485L89.959 46.0679L89.6612 45.8883L89.0149 45.8294L86.826 45.6353L92.9857 34.5193L82.9585 45.2887L74.8065 44.5627L71.4929 42.6L66.6049 39.2663L75.2949 27.4812L75.8617 26.7118L75.8171 26.1441L75.0477 16.5611L89.1787 1.00309L73.0097 15.449L72.7824 15.6681L72.7149 16.1663L71.4631 25.0725L60.6209 35.924L53.0546 39.4913L49.5027 35.7164L47.1302 37.8269L46.2913 29.3609L49.0312 14.4227L49.2109 13.4399L48.6966 12.8915L35.062 -4.60357e-10L45.1309 14.2721L40.8643 28.6291L40.8266 28.7556L40.7938 29.2798L40.1495 39.4604L23.8971 31.8257L23.2936 31.5448L23.0017 31.5467L2.91852 31.7726L21.8611 35.7531L33.5263 43.4235L31.7235 54.4112L-2.30997e-07 75.7377L33.0021 60.6605L38.7707 61.0032L37.0553 69.7279L23.9498 73.5027L36.424 72.9389L31.9995 95.451L43.4284 67.0652L44.3486 70.2917L53.4417 67.8404L71.9187 121.475L64.1062 82.4224L70.4674 70.9482L63.2922 78.6495L61.1311 68.6196L69.9731 70.3882L101.485 97.5808L73.2807 64.1467L70.3384 53.8213L81.6869 50.3332L88.5375 50.1652L100.931 55.1468L101.709 55.4577L102.154 54.9808L110.547 45.9433L101.351 52.9485ZM55.2742 60.3757L48.4663 63.2121L46.3241 55.6923L39.6105 55.2888L39.268 52.3316L36.3723 49.0327L41.723 41.315L46.7122 44.8697L51.366 42.0941L58.563 45.5938L61.8101 40.7869L68.1793 48.8309L62.8852 53.4168L65.3253 64.7201L55.2742 60.3757Z",
        transform: "translate(244.938 6.39355)"
    }))
;

const ErrorPage = (props)=>{
    const { getLabel  } = useIntl();
    const { header ='Oops, something went wrong...' , subheader , image =/*#__PURE__*/ React.createElement(BrokenGlasses, {
        ariaLabel: getLabel('common.errors.nonrecoverable.generic')
    }) , buttonText ,  } = props;
    return /*#__PURE__*/ React.createElement("div", {
        className: "error-page"
    }, header && /*#__PURE__*/ React.createElement(Heading, {
        className: "error-page__header"
    }, header), subheader && /*#__PURE__*/ React.createElement("p", {
        className: "error-page__sub-header"
    }, subheader), image && /*#__PURE__*/ React.createElement("div", {
        className: "error-page__image"
    }, image), /*#__PURE__*/ React.createElement("div", {
        className: "error-page__buttons"
    }, buttonText && /*#__PURE__*/ React.createElement(Button, {
        caption: buttonText,
        href: "/",
        className: "error-page__button"
    })));
};

const FlexLayout = ({ alignContent ='stretch' , alignItems ='stretch' , direction ='row' , justify ='flex-start' , wrap ='nowrap' , children , component: Component = 'div' ,  })=>{
    const classNames = classnames('flex-layout', `flex-layout--align-content-${alignContent}`, `flex-layout--align-items-${alignItems}`, `flex-layout--direction-${direction}`, `flex-layout--justify-${justify}`, `flex-layout--wrap-${wrap}`);
    return /*#__PURE__*/ React.createElement(Component, {
        className: classNames
    }, children);
};

const LocaleOption = ({ title , chooseLanguage , buttonCaption , path , locale , onSelect ,  })=>{
    const handleClick = ()=>{
        onSelect && onSelect(locale.replace('_', '-'), path);
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: "locale-option"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "locale-option__title"
    }, title), /*#__PURE__*/ React.createElement("div", {
        className: "locale-option__choose-language"
    }, chooseLanguage), /*#__PURE__*/ React.createElement(Button, {
        className: "locale-option__cta",
        caption: buttonCaption,
        onClick: handleClick
    }));
};

const LocaleSelectOverlay = ({ availableLocales , logo  })=>{
    const [open, setOpen] = React.useState(false);
    React.useEffect(()=>{
        const urlLocale = LocaleUtils.getLocaleFromUrl(window.location.href);
        const cookieLocale = LocaleUtils.getLocaleFromCookie();
        cookieLocale !== urlLocale ? setLocaleCookieFromUrl() : setOpen(!cookieLocale && availableLocales.length > 0);
    }, [
        availableLocales
    ]);
    const handleLocaleSelect = React.useCallback((selectedLocale, path)=>{
        LocaleUtils.setLocaleToCookie(selectedLocale);
        setOpen(false);
        window && window.location.assign(LocaleUtils.getLocaleSwitchPath(path));
    }, []);
    const setLocaleCookieFromUrl = ()=>{
        const urlLocale = LocaleUtils.getLocaleFromUrl(window.location.href);
        setOpen(!!urlLocale);
        urlLocale && LocaleUtils.setLocaleToCookie(urlLocale);
    };
    return /*#__PURE__*/ React.createElement(Modal, {
        isOpen: open,
        showIcon: false,
        className: "locale-select-overlay",
        "data-t": "locale-overlay"
    }, logo && /*#__PURE__*/ React.createElement("div", {
        className: "locale-select-overlay__logo-wrapper"
    }, /*#__PURE__*/ React.createElement(Logo, {
        url: "/",
        image: logo
    })), /*#__PURE__*/ React.createElement("div", {
        className: "locale-select-overlay__content",
        "data-t": "locale-overlay-content"
    }, availableLocales.map((option)=>/*#__PURE__*/ React.createElement(LocaleOption, swcHelpers.extends({
            key: `locale-option-${option.locale}`
        }, option, {
            onSelect: handleLocaleSelect
        }))
    )));
};

const getLocaleSwitcherData = (availableLocales, populateLink)=>{
    return availableLocales.map((option)=>{
        return {
            label: `${option.buttonCaption}`,
            link: populateLink ? {
                type: 'internal',
                href: option.path
            } : undefined,
            parameters: {
                action: ()=>{
                    LocaleUtils.setLocaleToCookie(option.locale.replace('_', '-'));
                    window && window.location.assign(LocaleUtils.getLocaleSwitchPath(option.path));
                }
            }
        };
    });
};

const getLocaleSwitcherMenuLabel = (fallbackLocale)=>{
    var ref;
    const selectedLocale = (ref = LocaleUtils.getLocaleFromCookie()) !== null && ref !== void 0 ? ref : fallbackLocale;
    const menuLabel = selectedLocale.substring(0, 2);
    return menuLabel.toUpperCase();
};

const getLocaleSwitcherMenuTitle = (availableLocales, locale, fallbackLabel)=>{
    const selectedLocale = availableLocales.find((item)=>item.locale.replace('_', '-') === locale
    );
    return selectedLocale ? selectedLocale.chooseLanguage : fallbackLabel;
};

const createSubMenu = (items)=>{
    return items.map((item, index)=>{
        var ref, ref1;
         return React.createElement(Flyout.MenuItem, {
            key: item.label || index,
            link: item === null || item === void 0 ? void 0 : (ref = item.link) === null || ref === void 0 ? void 0 : ref.href,
            action: item === null || item === void 0 ? void 0 : (ref1 = item.parameters) === null || ref1 === void 0 ? void 0 : ref1.action
        }, item.label);
    });
};
const LocaleSwitcherMenu = ({ availableLocales , className  })=>{
    const intl = useIntl();
    const localeSwitcherData = getLocaleSwitcherData(availableLocales, true);
    const menuLabel = getLocaleSwitcherMenuLabel(intl.locale);
    const menuTitle = getLocaleSwitcherMenuTitle(availableLocales, intl.locale, menuLabel);
    return /*#__PURE__*/ React.createElement(Flyout, null, /*#__PURE__*/ React.createElement(Flyout.Trigger, {
        className: classnames('locale-switcher-menu__button--link header-menu__icon', className),
        title: menuTitle
    }, menuLabel), /*#__PURE__*/ React.createElement(Flyout.Menu, null, createSubMenu(localeSwitcherData)));
};

function HeaderIconWithLabel({ icon  }) {
    const { getLabel  } = useIntl();
    if (!icon.parameters || !icon.parameters.icon) {
        return null;
    }
    const href = icon.link && icon.link.href ? icon.link.href : '#';
    const iconLabel = icon.parameters.icon === 'location' ? getLabel('stores.form.findStore.title') : icon.label;
    return /*#__PURE__*/ React.createElement("a", {
        className: "header-menu__icon",
        href: href,
        "aria-label": iconLabel
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: icon.parameters.icon
    }), /*#__PURE__*/ React.createElement("span", {
        className: "header-menu__label"
    }, iconLabel));
}
const HeaderMenu = ({ renderCartButton , showMultilingualComponents , availableLocales , myAccountButton , menuItems , toggleMobileSearch , countrySwitcherOptions , className , ordersUrl ,  })=>{
    const isAboveTabletPortrait = MediaQuery.useIsAbove(MediaQuery.BreakpointName.TABLET_PORTRAIT);
    const { getLabel  } = useIntl();
    const searchLabel = getLabel('common.header.search', {
        hideLabelKey: true
    });
    const { isReorderButtonEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement("nav", {
        className: classnames('header-menu', className)
    }, /*#__PURE__*/ React.createElement("div", {
        className: "header-menu__icons"
    }, /*#__PURE__*/ React.createElement("button", {
        type: "button",
        className: "header-menu__search-button header-menu__icon",
        onClick: toggleMobileSearch,
        "aria-label": searchLabel
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "search"
    }), searchLabel && /*#__PURE__*/ React.createElement("span", {
        className: "header-menu__label"
    }, searchLabel)), isAboveTabletPortrait && /*#__PURE__*/ React.createElement(React.Fragment, null, countrySwitcherOptions && /*#__PURE__*/ React.createElement(CountrySwitcherMenu, {
        options: countrySwitcherOptions
    }), showMultilingualComponents && /*#__PURE__*/ React.createElement(LocaleSwitcherMenu, {
        availableLocales: availableLocales ? availableLocales : []
    }), myAccountButton, isReorderButtonEnabled && ordersUrl && /*#__PURE__*/ React.createElement(ReorderButton, {
        url: ordersUrl
    })), menuItems.map((item)=>item.parameters && item.parameters.icon !== 'cart' ? /*#__PURE__*/ React.createElement(HeaderIconWithLabel, {
            key: item.label,
            icon: item
        }) : renderCartButton ? renderCartButton(item) : /*#__PURE__*/ React.createElement(CartButton, {
            key: item.label,
            href: item.link && item.link.href,
            className: "header-menu__icon",
            label: item.label
        })
    )));
};

const Header$1 = ({ renderCartButton , availableLocales , algoliaHeaderConfig , menuItems , myAccountButton , logo , logoMobile , logoLink , formatProductUrl , pageRoutes , onSubmit , staticsUrl , preview , countrySwitcherOptions , children , renderSiteNav =true , hasOnlyLogo =false , siteName , className , ordersUrl ,  })=>{
    const [searchHasFocus, setSearchHasFocus] = React.useState(false);
    const { getLabel  } = useIntl();
    const { isMultilingualEnabled  } = useFeatureToggles();
    const openSiteNavEvent = React.useRef();
    const currentBreakpoint = MediaQuery.useBreakpoint();
    const isMobile = currentBreakpoint === MediaQuery.BreakpointName.TABLET_PORTRAIT || currentBreakpoint === MediaQuery.BreakpointName.PHONE;
    const { isHidden , isCondensedHeader , headerRef  } = Util.useHeaderOnScroll(isMobile);
    /** Register sitenav.open event, needs window */ React.useEffect(()=>{
        openSiteNavEvent.current = Util.createCustomEvent('sitenav.open');
    }, []);
    const headerClasses = classnames('header__header', {
        'header__header--hidden': isHidden && isMobile,
        'header__header--search-has-focus': searchHasFocus
    });
    const showMultilingualComponents = !hasOnlyLogo && isMultilingualEnabled && !preview && !!availableLocales && availableLocales.length > 1;
    const logoLinkPath = logoLink ? logoLink : (pageRoutes === null || pageRoutes === void 0 ? void 0 : pageRoutes.root) || '/';
    const logoTitle = siteName ? `${siteName} home` : 'Home';
    const handleFocus = React.useCallback(()=>{
        setSearchHasFocus(true);
    }, []);
    const handleClose = React.useCallback(()=>{
        setSearchHasFocus(false);
    }, []);
    const menuLabel = getLabel('common.header.menu', {
        hideLabelKey: true
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('header', className, {
            'is-header-condensed': isCondensedHeader
        })
    }, /*#__PURE__*/ React.createElement("header", {
        className: headerClasses,
        role: "banner",
        ref: headerRef
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames('site-container header__container', {
            'header__container--center': hasOnlyLogo
        })
    }, !hasOnlyLogo && renderSiteNav && /*#__PURE__*/ React.createElement("button", {
        className: "header__hamburger",
        onClick: toggleSiteNav,
        type: "button",
        "aria-label": getLabel('common.menu.open')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "hamburger"
    }), menuLabel && /*#__PURE__*/ React.createElement("span", {
        className: "header-menu__label header-menu__label--hamburger"
    }, menuLabel)), /*#__PURE__*/ React.createElement("div", {
        className: "header__logo-wrapper"
    }, /*#__PURE__*/ React.createElement(Logo, {
        className: isMobile ? 'header__logo--mobile' : 'header__logo',
        url: logoLinkPath,
        image: isMobile ? logoMobile : logo,
        title: logoTitle,
        srOnlyText: siteName
    })), !hasOnlyLogo && /*#__PURE__*/ React.createElement(React.Fragment, null, isMobile && renderSearchBar(), /*#__PURE__*/ React.createElement(HeaderMenu, {
        renderCartButton: renderCartButton,
        showMultilingualComponents: showMultilingualComponents,
        availableLocales: availableLocales,
        myAccountButton: myAccountButton,
        menuItems: menuItems,
        toggleMobileSearch: handleFocus,
        countrySwitcherOptions: countrySwitcherOptions,
        ordersUrl: ordersUrl
    }), !isMobile && renderSearchBar()))), showMultilingualComponents && /*#__PURE__*/ React.createElement(LocaleSelectOverlay, {
        logo: logoMobile ? logoMobile : logo,
        availableLocales: availableLocales !== null && availableLocales !== void 0 ? availableLocales : []
    }), children);
    /**
   * event dispatched for opening the sitenavigation on mobile
   */ function toggleSiteNav() {
        openSiteNavEvent.current && window.dispatchEvent(openSiteNavEvent.current);
    }
    /**
   * Render SearchBar component
   */ function renderSearchBar() {
        if (!algoliaHeaderConfig) {
            return null;
        }
        const { apiKey , appId  } = algoliaHeaderConfig;
        if (!apiKey || !appId || !onSubmit || !formatProductUrl || !staticsUrl) {
            return null;
        }
        const searchClassName = classnames('header__search', {
            'header__search--mobile': isMobile,
            'header__search--desktop': !isMobile,
            'header__search--focus': searchHasFocus,
            'header__search--condensed': isCondensedHeader
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: searchClassName,
            role: "search"
        }, /*#__PURE__*/ React.createElement(SearchBar, {
            isMultilingualEnabled: isMultilingualEnabled,
            pageRoutes: pageRoutes,
            formatProductUrl: formatProductUrl,
            onSubmit: onSubmit,
            staticsUrl: staticsUrl,
            config: algoliaHeaderConfig,
            onFocus: handleFocus,
            onClose: handleClose,
            forceFocus: searchHasFocus,
            isMobile: isMobile
        }));
    }
};

const getButtonVariant = (item)=>{
    var ref, ref1, ref2;
    return ((ref = item.parameters) === null || ref === void 0 ? void 0 : ref.buttonVariant) && [
        'solid',
        'ghost',
        'outline'
    ].indexOf((ref1 = item.parameters) === null || ref1 === void 0 ? void 0 : ref1.buttonVariant) > -1 ? (ref2 = item.parameters) === null || ref2 === void 0 ? void 0 : ref2.buttonVariant : 'solid';
};
const getButtonColor = (item)=>{
    var ref, ref3, ref4;
    return ((ref = item.parameters) === null || ref === void 0 ? void 0 : ref.buttonColor) && [
        'primary',
        'secondary'
    ].indexOf((ref3 = item.parameters) === null || ref3 === void 0 ? void 0 : ref3.buttonColor) > -1 ? (ref4 = item.parameters) === null || ref4 === void 0 ? void 0 : ref4.buttonColor : 'primary';
};

// We need to stop this event from bubbling up in order for the href to be executed on click.
const handleClick = (event)=>{
    event.stopPropagation();
};
const renderIcon = (name)=>name ? /*#__PURE__*/ React.createElement("span", {
        className: "submenu__icon"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: name
    })) : null
;
const SubMenuLinks$1 = ({ items , ariaLabel , className , level =1 , handleKeyDown , tabIndex  })=>{
     return React.createElement("ul", {
        "aria-label": ariaLabel,
        className: classnames('submenu__items', `submenu__level-${level}`)
    }, items.map((item)=>{
        var ref6, ref1, ref2, ref3, ref4;
        if (!item.label) {
            return null;
        }
        let href = '';
        if (item.link && item.link.href) {
            href = item.link.href;
        }
        if (item.parameters && item.parameters.queryString && item.parameters.queryString.length) {
            href += item.parameters.queryString;
        }
        let action;
        if (item.parameters && item.parameters.action) {
            action = item.parameters.action;
            href = '';
        }
        const icon = (item === null || item === void 0 ? void 0 : (ref6 = item.parameters) === null || ref6 === void 0 ? void 0 : ref6.icon) || '';
        const classes = classnames('submenu__item', className, ((ref1 = item.link) === null || ref1 === void 0 ? void 0 : ref1.active) && 'submenu__item--active');
        const onClick = ((ref2 = item.link) === null || ref2 === void 0 ? void 0 : ref2.onClick) ? (e)=>{
            var ref, ref5;
            return (ref = item.link) === null || ref === void 0 ? void 0 : (ref5 = ref.onClick) === null || ref5 === void 0 ? void 0 : ref5.call(ref, e), handleClick(e);
        } : handleClick;
        return /*#__PURE__*/ React.createElement("li", {
            className: classes,
            key: item.label
        }, action && !href && /*#__PURE__*/ React.createElement("button", {
            className: "submenu__link",
            onClick: action,
            onKeyDown: handleKeyDown,
            tabIndex: tabIndex
        }, renderIcon(icon), item.label), href && !action && /*#__PURE__*/ React.createElement("a", {
            className: "submenu__link",
            onClick: onClick,
            href: href,
            "data-t": ((ref3 = item.parameters) === null || ref3 === void 0 ? void 0 : ref3.testId) ? `submenu-link-${item.parameters.testId}` : undefined,
            onKeyDown: handleKeyDown,
            tabIndex: tabIndex
        }, renderIcon(icon), item.label), !href && !action && /*#__PURE__*/ React.createElement("p", null, renderIcon(icon), item.label), ((ref4 = item.childMenuItems) === null || ref4 === void 0 ? void 0 : ref4.length) ? /*#__PURE__*/ React.createElement(SubMenuLinks$1, {
            items: item.childMenuItems,
            ariaLabel: item.label,
            level: 2,
            handleKeyDown: handleKeyDown,
            tabIndex: tabIndex
        }) : null);
    }));
};
/**
 * No opcos use this menu style on production anymore,
 * However it's still used on some test/acct environments.
 * We should update the content in BR on those environments before removing this.
 * @deprecated
 */ const getCategoryLinksItem = (item)=>{
    const link = item.link && item.link.href || '';
    const imageUrl = item.parameters && item.parameters.image || '';
    return {
        title: item.label,
        link: {
            url: link,
            target: 'internal'
        },
        image: {
            url: imageUrl,
            dimensions: {
                w: 200,
                h: 170
            }
        }
    };
};
const hasCategoryLinks = (menuItems)=>{
    const imageItems = menuItems.filter((item)=>item.parameters && item.parameters.image
    );
    return !!imageItems.length;
};
const SubMenu$1 = ({ menuItems , isActive =false , closeSubMenu  })=>{
    const hasImages = hasCategoryLinks(menuItems);
    const handleKeyDown = (event)=>{
        if (event.key === 'Escape') {
            closeSubMenu();
        }
    };
    const tabIndex = isActive ? undefined : -1;
    return /*#__PURE__*/ React.createElement("div", {
        "data-t": "site-nav-submenu",
        "aria-hidden": !isActive,
        className: classnames('submenu', {
            'submenu--with-image': hasImages,
            _is_active: isActive
        })
    }, /*#__PURE__*/ React.createElement("div", {
        className: "submenu__content"
    }, menuItems.map((item)=>{
        var ref;
         return React.createElement("div", {
            className: "submenu__column",
            key: item.label
        }, item.parameters && item.parameters.image && /*#__PURE__*/ React.createElement(CategoryLinks, null, "[", /*#__PURE__*/ React.createElement(CategoryLinksItem, swcHelpers.extends({}, getCategoryLinksItem(item))), "]"), item.link && item.link.href ? /*#__PURE__*/ React.createElement("a", {
            className: "submenu__link",
            href: item.link.href,
            "data-t": "submenu-title-link",
            onClick: handleClick,
            onKeyDown: handleKeyDown,
            tabIndex: tabIndex
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "submenu__title",
            component: "div",
            visualHeadingOnly: true
        }, item.label)) : /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "submenu__title",
            component: "div",
            visualHeadingOnly: true
        }, item.label), ((ref = item.childMenuItems) === null || ref === void 0 ? void 0 : ref.length) ? /*#__PURE__*/ React.createElement(SubMenuLinks$1, {
            items: item.childMenuItems,
            ariaLabel: item.label,
            handleKeyDown: handleKeyDown,
            tabIndex: tabIndex
        }) : null);
    })));
};

const SiteNavItem = ({ label , link , childMenuItems =[] , parameters , focusCallback , activeSubMenu , setActiveSubMenu  })=>{
    const hasSubmenu = (childMenuItems === null || childMenuItems === void 0 ? void 0 : childMenuItems.length) > 0;
    const shouldHandleSubmenu = !Util.isTouchDevice() && hasSubmenu;
    const linkRef = React.useRef();
    const isActive = React.useMemo(()=>activeSubMenu === label
    , [
        activeSubMenu,
        label
    ]);
    const [mouseOver, setMouseOver] = React.useState(false);
    /**
   * On cursor events, delay the open and closing of the submenus with 0.4s and 0.2s to prevent users
   * from opening the submenu accidentally when moving their cursor over the site nav
   */ React.useEffect(()=>{
        if (isActive === mouseOver) {
            return;
        }
        const ms = mouseOver ? 400 : 200;
        const timeout = setTimeout(()=>setActiveSubMenu(mouseOver ? label : '')
        , ms);
        return ()=>clearTimeout(timeout)
        ;
    }, [
        mouseOver
    ]);
    const closeSubMenu = React.useMemo(()=>{
        return ()=>{
            var ref;
            setActiveSubMenu('');
            (ref = linkRef.current) === null || ref === void 0 ? void 0 : ref.focus();
        };
    }, [
        setActiveSubMenu,
        linkRef
    ]);
    const handleMouseOver = ()=>setMouseOver(true)
    ;
    const handleMouseLeave = ()=>setMouseOver(false)
    ;
    const handleKeyDown = React.useCallback((event)=>{
        if (event.key === 'Escape') {
            closeSubMenu();
        }
        // Handle Enter and Space keys
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            setActiveSubMenu(activeSubMenu === label ? '' : label);
        }
    }, [
        closeSubMenu,
        setActiveSubMenu,
        label
    ]);
    return(// We are disabling the a11y linter rule which requires onMouseOver to be used with onFocus.
    // The onFocus is implemented on the Link element, therefore the rule is met but the linter cant tell.
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    /*#__PURE__*/ React.createElement("li", {
        className: classnames('site-nav-item', {
            [`site-nav-item--${parameters === null || parameters === void 0 ? void 0 : parameters.menuKey}`]: !!(parameters === null || parameters === void 0 ? void 0 : parameters.menuKey),
            _is_active: link === null || link === void 0 ? void 0 : link.active
        }),
        "data-t": "site-nav-item",
        onMouseOver: shouldHandleSubmenu ? handleMouseOver : undefined,
        onMouseLeave: shouldHandleSubmenu ? handleMouseLeave : undefined
    }, /*#__PURE__*/ React.createElement(Link, {
        className: "site-nav-item__link",
        "data-t": "site-nav-item-link",
        url: link === null || link === void 0 ? void 0 : link.href,
        openInNewWindow: link === null || link === void 0 ? void 0 : link.openInNewWindow,
        // Handle keyboard usage
        onKeyDown: shouldHandleSubmenu ? handleKeyDown : undefined,
        onFocus: focusCallback,
        ref: linkRef,
        ariaHasPopup: hasSubmenu,
        ariaIsExpanded: isActive
    }, label, hasSubmenu && /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-down",
        height: 18,
        width: 18
    })), hasSubmenu && /*#__PURE__*/ React.createElement(SubMenu$1, {
        menuItems: childMenuItems,
        isActive: isActive,
        closeSubMenu: closeSubMenu
    })));
};

const Buttons = ({ active , items  })=>{
    if (!items || !items.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "site-nav-mobile__buttons"
    }, items.map((item)=>/*#__PURE__*/ React.createElement(Button, {
            key: item.label,
            "data-t": item.label,
            icon: item.parameters && item.parameters.icon,
            href: item.link && item.link.href,
            caption: item.label,
            variant: getButtonVariant(item),
            color: getButtonColor(item),
            tabIndex: active ? undefined : -1
        })
    ));
};

const buttonContainer = {
    show: {
        opacity: 1,
        transition: {
            ease: 'easeOut',
            duration: 0.3
        }
    },
    hide: {
        opacity: 0,
        transition: {
            duration: 0
        }
    }
};
const Header = ({ activeSubMenu , backLabel , closeLabel , countrySwitcherOptions , hideSubMenu , hideSiteNav , isOpen , routes , renderCartButton , ordersUrl  })=>{
    const { isReorderButtonEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement("div", {
        className: "site-nav-mobile__header"
    }, isOpen && /*#__PURE__*/ React.createElement(motion.button, {
        animate: activeSubMenu ? 'show' : 'hide',
        className: "site-nav-mobile__back-button",
        onClick: hideSubMenu,
        variants: buttonContainer,
        "aria-hidden": activeSubMenu === '',
        tabIndex: isOpen && activeSubMenu !== '' ? undefined : -1
    }, /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-left"
    }), /*#__PURE__*/ React.createElement("span", {
        className: "site-nav-mobile__sub-menu-title"
    }, backLabel))), countrySwitcherOptions && /*#__PURE__*/ React.createElement(CountrySwitcherMenu, {
        className: "site-nav-mobile__icon",
        options: isOpen ? countrySwitcherOptions : [],
        tabIndex: isOpen ? undefined : -1,
        ariaHidden: !isOpen
    }), isReorderButtonEnabled && ordersUrl && /*#__PURE__*/ React.createElement(ReorderButton, {
        tabIndex: isOpen ? undefined : -1,
        "aria-hidden": !isOpen,
        url: ordersUrl
    }), /*#__PURE__*/ React.createElement("a", {
        className: "site-nav-mobile__icon",
        href: routes.stores,
        tabIndex: isOpen ? undefined : -1
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "location"
    })), renderCartButton ? renderCartButton({
        label: '',
        link: {
            href: routes.cart,
            type: 'internal'
        }
    }, isOpen ? undefined : -1) : /*#__PURE__*/ React.createElement(CartButton, {
        className: "site-nav-mobile__icon site-nav-mobile__cart",
        href: routes.cart,
        tabIndex: isOpen ? undefined : -1
    }), /*#__PURE__*/ React.createElement("button", {
        className: "site-nav-mobile__icon site-nav-mobile__close",
        onClick: hideSiteNav,
        "aria-label": closeLabel,
        tabIndex: isOpen ? undefined : -1
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "close"
    })));
};

const Item = ({ childMenuItems =[] , label , link , onItemClick , parameters , tabIndex , linkRef ,  })=>{
    const hasChildMenuItems = childMenuItems && childMenuItems.length > 0;
    const hasClickHandler = onItemClick && hasChildMenuItems;
    const handleClick = React.useCallback((event)=>{
        if (hasClickHandler) {
            event.preventDefault();
            onItemClick && onItemClick(label);
        }
    }, [
        hasClickHandler,
        label
    ]);
    const menuKey = parameters && parameters.menuKey || '';
    const classes = classnames('site-nav-mobile__item', {
        [`site-nav-mobile__item--${menuKey}`]: menuKey,
        'site-nav-mobile_is_active': link && link.active
    });
    return /*#__PURE__*/ React.createElement("li", {
        className: classes
    }, /*#__PURE__*/ React.createElement("a", {
        className: "site-nav-mobile__link",
        href: link === null || link === void 0 ? void 0 : link.href,
        target: (link === null || link === void 0 ? void 0 : link.openInNewWindow) ? '_blank' : '_self',
        onClick: handleClick,
        rel: "noreferrer",
        tabIndex: tabIndex,
        "aria-haspopup": !!hasChildMenuItems,
        ref: linkRef
    }, label), hasChildMenuItems && /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-right"
    }));
};

const menuContainer = {
    visible: {
        x: '0%',
        transition: {
            ease: 'easeIn',
            duration: 0.3
        }
    },
    hidden: {
        x: '-100%',
        transition: {
            ease: 'easeOut',
            duration: 0.3
        }
    }
};
const Menu = ({ active , children , menuItems , onItemClick , firstMenuRef , isOpen ,  })=>/*#__PURE__*/ React.createElement(motion.div, {
        className: "site-nav-mobile__page",
        animate: active ? 'visible' : 'hidden',
        variants: menuContainer,
        "aria-hidden": !active
    }, children, /*#__PURE__*/ React.createElement("ul", {
        className: "site-nav-mobile__menu"
    }, menuItems.map((item, index)=>/*#__PURE__*/ React.createElement(Item, swcHelpers.extends({
            key: item.label,
            onItemClick: onItemClick,
            tabIndex: isOpen && active ? undefined : -1,
            linkRef: index === 0 ? firstMenuRef : undefined
        }, item))
    )))
;

// We need to stop this event from bubbling up in order for the href to be executed on click.
const handleHrefClick = (event)=>{
    event.stopPropagation();
};

const SubMenuLinks = ({ items , className , level =1 , ariaLabel  })=>{
     return React.createElement("ul", {
        className: classnames('site-nav-mobile-submenu__items', `site-nav-mobile-submenu__level-${level}`),
        "aria-label": ariaLabel
    }, items.map((item)=>{
        var ref, ref1, ref2, ref3;
        if (!item.label) {
            return null;
        }
        let href = '#';
        if (item.link && item.link.href) {
            href = item.link.href;
        }
        if (item.parameters && item.parameters.queryString && item.parameters.queryString.length) {
            href += item.parameters.queryString;
        }
        let action;
        if (item.parameters && item.parameters.action) {
            action = item.parameters.action;
            href = '';
        }
        const icon = (item === null || item === void 0 ? void 0 : (ref = item.parameters) === null || ref === void 0 ? void 0 : ref.icon) || '';
        const classes = classnames('site-nav-mobile-submenu__item', ((ref1 = item.link) === null || ref1 === void 0 ? void 0 : ref1.active) && 'site-nav-mobile-submenu__item--active', icon && 'site-nav-mobile-submenu__item-with-icon', className);
        return /*#__PURE__*/ React.createElement("li", {
            className: classes,
            key: item.label
        }, action && !href && /*#__PURE__*/ React.createElement("button", {
            className: "site-nav-mobile-submenu__link",
            onClick: action
        }, icon ? /*#__PURE__*/ React.createElement("span", {
            className: "site-nav-mobile-submenu__icon"
        }, /*#__PURE__*/ React.createElement(Icon, {
            name: icon
        })) : null, item.label), href && /*#__PURE__*/ React.createElement("a", {
            className: "site-nav-mobile-submenu__link",
            onClick: handleHrefClick,
            href: href,
            "data-t": ((ref2 = item.parameters) === null || ref2 === void 0 ? void 0 : ref2.testId) ? `submenu-link-${item.parameters.testId}` : undefined
        }, icon ? /*#__PURE__*/ React.createElement("span", {
            className: "site-nav-mobile-submenu__icon"
        }, /*#__PURE__*/ React.createElement(Icon, {
            name: icon
        })) : null, item.label), ((ref3 = item.childMenuItems) === null || ref3 === void 0 ? void 0 : ref3.length) ? /*#__PURE__*/ React.createElement(SubMenuLinks, {
            items: item.childMenuItems,
            level: 2
        }) : null);
    }));
};

const subMenuContainer = {
    hidden: {
        x: '0%',
        // Instead of 1 SubMenu multiple can be rendered, make sure to hide the inactive ones
        display: 'none',
        transition: {
            ease: 'easeIn',
            duration: 0.3
        },
        transitionEnd: {
            display: 'none'
        }
    },
    visible: {
        x: '-100%',
        display: 'flex',
        transition: {
            duration: 0.3,
            ease: 'easeOut'
        }
    }
};
const SubMenu = ({ active , menuItems , hideSubMenu  })=>{
    const firstLink = React__default.useRef(null);
    // TODO: find a way to make this work for keyboard and screenreader users
    // without needing to focus() also in mouse or regular mobile usage.
    // Please do not remove without testing with both keyboard and screen reader mode (Cmd+F5)
    useEffect(()=>{
        if (active) {
            setTimeout(()=>{
                var ref;
                return (ref = firstLink.current) === null || ref === void 0 ? void 0 : ref.focus();
            }, 50);
        }
    }, [
        active
    ]);
    const keyUp = useCallback((e)=>{
        if (e.key === 'Escape') {
            hideSubMenu(true);
            e.stopPropagation();
        }
    }, [
        hideSubMenu
    ]);
    return /*#__PURE__*/ React__default.createElement(motion.div, {
        className: "site-nav-mobile__page",
        animate: active ? 'visible' : 'hidden',
        variants: subMenuContainer,
        "aria-hidden": !active
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "site-nav-mobile-submenu",
        onKeyUp: keyUp,
        role: "presentation"
    }, menuItems.map(({ childMenuItems , label , link , parameters  }, index)=>{
         return React__default.createElement("div", {
            className: "site-nav-mobile-submenu__column",
            key: label
        }, (link === null || link === void 0 ? void 0 : link.href) ? /*#__PURE__*/ React__default.createElement("a", {
            className: "site-nav-mobile-submenu__link--mobile",
            onClick: handleHrefClick,
            href: link.href,
            ref: index === 0 ? firstLink : null
        }, (parameters === null || parameters === void 0 ? void 0 : parameters.icon) ? /*#__PURE__*/ React__default.createElement("span", {
            className: "site-nav-mobile-submenu__icon"
        }, /*#__PURE__*/ React__default.createElement(Icon, {
            name: parameters.icon
        })) : null, label) : /*#__PURE__*/ React__default.createElement(Heading, {
            variant: "small",
            className: "site-nav-mobile-submenu__title",
            component: "p"
        }, label), (childMenuItems === null || childMenuItems === void 0 ? void 0 : childMenuItems.length) ? /*#__PURE__*/ React__default.createElement(SubMenuLinks, {
            items: childMenuItems,
            ariaLabel: label
        }) : null);
    })));
};

/**
 * TODO:
 *
 * CTA buttons should be configurable as "persistent" buttons so it can be shown in a submenu when needed
 * CTA buttons should be configurable as sticky so it either scrolls out of view or stays on top
 *
 * Allow for different menu items based on device. (Normalize menu items data too)
 *
 * Right now a hardcoded my-account css hack is implemented. (existing code)
 *    &--my-account {
 *      display: none;
 *    }
 */ const siteNavContainer = {
    open: {
        x: '0%',
        transition: {
            ease: 'easeInOut',
            duration: 0.3
        },
        opacity: 1
    },
    closed: {
        x: '-100%',
        transition: {
            ease: 'easeInOut',
            duration: 0.3
        }
    }
};
const SiteNavMobile = ({ ctaItems , countrySwitcherOptions , menuItems , routes , renderCartButton , ordersUrl  })=>{
    const { getLabel  } = useIntl();
    const [isOpen, setIsOpen] = React__default.useState(false);
    const [activeSubMenu, setActiveSubMenu] = React__default.useState('');
    const firstMenuRef = React__default.useRef(null);
    const siteNavContainerRef = React__default.useRef(null);
    const handler = React__default.useCallback(()=>{
        setIsOpen(true);
    }, []);
    useEventListener('sitenav.open', handler);
    React__default.useEffect(()=>{
        if (isOpen) {
            Util.lockScroll();
            return ()=>{
                Util.unlockScroll();
                setActiveSubMenu('');
            };
        }
    }, [
        isOpen
    ]);
    const hideSiteNav = React__default.useCallback(()=>{
        setIsOpen(false);
    }, []);
    const hideSubMenu = React__default.useCallback((focusFirstElement = false)=>{
        setActiveSubMenu('');
        if (focusFirstElement) {
            setTimeout(()=>{
                var ref;
                return (ref = firstMenuRef.current) === null || ref === void 0 ? void 0 : ref.focus();
            }, 50);
        }
    }, [
        setActiveSubMenu
    ]);
    const subMenus = React__default.useMemo(()=>menuItems.filter((item)=>item.childMenuItems
        ).map((item)=>[
                item.label,
                item.childMenuItems
            ]
        )
    , [
        menuItems
    ]);
    const keyUp = useCallback((e)=>{
        if (e.key === 'Escape') hideSiteNav();
    }, [
        hideSiteNav
    ]);
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Backdrop, {
        className: "site-nav-mobile__backdrop",
        isVisible: isOpen,
        onClick: hideSiteNav
    }), /*#__PURE__*/ React__default.createElement(FocusTrap, {
        active: isOpen,
        focusTrapOptions: {
            allowOutsideClick: true,
            escapeDeactivates: false
        }
    }, /*#__PURE__*/ React__default.createElement(motion.nav, {
        animate: isOpen ? 'open' : 'closed',
        initial: "closed",
        className: classnames('site-nav-mobile', {
            'site-nav-mobile--open': isOpen
        }),
        variants: siteNavContainer,
        ref: siteNavContainerRef,
        onKeyUp: keyUp,
        "aria-hidden": !isOpen
    }, /*#__PURE__*/ React__default.createElement(Header, {
        activeSubMenu: activeSubMenu,
        countrySwitcherOptions: countrySwitcherOptions,
        backLabel: getLabel('common.actions.previous'),
        closeLabel: getLabel('common.actions.close'),
        hideSiteNav: hideSiteNav,
        hideSubMenu: hideSubMenu,
        isOpen: isOpen,
        renderCartButton: renderCartButton,
        ordersUrl: ordersUrl,
        routes: routes
    }), /*#__PURE__*/ React__default.createElement("div", {
        className: "site-nav-mobile__content"
    }, /*#__PURE__*/ React__default.createElement(Menu, {
        active: !activeSubMenu,
        menuItems: menuItems,
        onItemClick: setActiveSubMenu,
        firstMenuRef: firstMenuRef,
        isOpen: isOpen
    }, /*#__PURE__*/ React__default.createElement(Buttons, {
        items: ctaItems,
        active: !activeSubMenu && isOpen
    })), subMenus.map(([label, subMenuItems])=>/*#__PURE__*/ React__default.createElement(SubMenu, {
            key: label,
            active: activeSubMenu === label,
            menuItems: subMenuItems,
            hideSubMenu: hideSubMenu
        })
    )))));
};

const SiteNav = ({ menuItems , appointmentItems , routes , countrySwitcherOptions , renderCartButton , ordersUrl ,  })=>{
    const breakpoint = MediaQuery.useBreakpoint();
    const isMobile = breakpoint === MediaQuery.BreakpointName.PHONE || breakpoint === MediaQuery.BreakpointName.TABLET_PORTRAIT;
    const [activeSubMenu, setActiveSubMenu] = React__default.useState('');
    /**
   * This onBlur handler is to catch two edge cases:
   * * tab on the last focusable element on the last submenu (would not be caught
   *   by an onFocus of another main menu entry like other submenus
   * * shift-tab on the first focusable element on the first submenu (when, of
   *   course, the first main entry is one with a submenu)
   * The handler checks if focus has fallen out of the complete site-nav__menu element
   */ const onBlur = useCallback((e)=>{
        const currentTarget = e.currentTarget;
        requestAnimationFrame(()=>{
            if (!currentTarget.contains(document.activeElement)) setActiveSubMenu('');
        });
    }, [
        setActiveSubMenu
    ]);
    const focusCallback = (label)=>(event)=>{
            if (activeSubMenu !== label) setActiveSubMenu('');
        }
    ;
    if (isMobile) {
        return /*#__PURE__*/ React__default.createElement(SiteNavMobile, {
            routes: routes,
            ctaItems: appointmentItems,
            menuItems: menuItems,
            countrySwitcherOptions: countrySwitcherOptions,
            renderCartButton: renderCartButton,
            ordersUrl: ordersUrl
        });
    }
    return /*#__PURE__*/ React__default.createElement("nav", {
        className: "site-nav",
        "data-t": "site-nav"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "site-container site-nav__container"
    }, /*#__PURE__*/ React__default.createElement("ul", {
        className: "site-nav__menu",
        onBlur: onBlur
    }, menuItems.map((item)=>/*#__PURE__*/ React__default.createElement(SiteNavItem, swcHelpers.extends({
            key: item.label
        }, item, {
            focusCallback: focusCallback(item.label),
            activeSubMenu: activeSubMenu,
            setActiveSubMenu: setActiveSubMenu
        }))
    )), appointmentItems && /*#__PURE__*/ React__default.createElement("div", {
        className: "site-nav__appointment",
        "data-t": "site-nav-appointment"
    }, appointmentItems.map((item)=>{
        var ref, ref1;
         return React__default.createElement(Button, {
            key: item.label,
            icon: (ref = item.parameters) === null || ref === void 0 ? void 0 : ref.icon,
            className: ((ref1 = item.parameters) === null || ref1 === void 0 ? void 0 : ref1.icon) && 'site-nav__appointment-btn',
            href: item.link && item.link.href,
            caption: item.label,
            variant: getButtonVariant(item),
            color: getButtonColor(item)
        });
    }))));
};

const SplitPane = ({ main , aside , mainWidth =50 , flip  })=>{
    const asideWidth = 100 - mainWidth;
    const mainPosition = flip ? 'second' : 'first';
    const asidePosition = flip ? 'first' : 'second';
    const mainClasses = classnames('split-pane__main', `split-pane__main--ratio-${mainWidth}`, `split-pane--${mainPosition}`);
    const asideClasses = classnames('split-pane__aside', `split-pane__aside--ratio-${asideWidth}`, `split-pane--${asidePosition}`);
    return /*#__PURE__*/ React.createElement("article", {
        className: "split-pane",
        "data-t": "split-pane"
    }, main && /*#__PURE__*/ React.createElement("div", {
        className: mainClasses,
        "data-t": "main-pane"
    }, main), aside && /*#__PURE__*/ React.createElement("aside", {
        className: asideClasses,
        "data-t": "side-pane"
    }, aside));
};

const PageLayout = ({ children , header , preFooter , footer , fixedBottomBarContent , hasSiteNav =true , hasOnlyLogoInHeader =false ,  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('page-layout', {
            'page-layout--with-header': !!header,
            'page-layout--with-nav': hasSiteNav,
            'page-layout--with-onlyLogoInHeader': hasOnlyLogoInHeader
        })
    }, /*#__PURE__*/ React__default.createElement("a", {
        className: "page-layout__skip-link",
        href: "#main-content"
    }, getLabel('common.skipLink')), header && /*#__PURE__*/ React__default.createElement("div", {
        className: "page-layout__header"
    }, header), /*#__PURE__*/ React__default.createElement("main", {
        role: "main",
        id: "main-content",
        className: "page-layout__main"
    }, children, preFooter && /*#__PURE__*/ React__default.createElement("div", {
        className: "page-layout__pre-footer"
    }, preFooter)), footer && /*#__PURE__*/ React__default.createElement("div", {
        className: "page-layout__footer"
    }, footer), fixedBottomBarContent);
};

export { CountrySwitcherMenu, ErrorPage, FlexLayout, Footer, FooterLinks, Header$1 as Header, LocaleSwitcherMenu, PageLayout, SiteNav, SplitPane, SubMenu$1 as SubMenu, SubMenuLinks$1 as SubMenuLinks, getLocaleSwitcherData, getLocaleSwitcherMenuLabel, getLocaleSwitcherMenuTitle };
//# sourceMappingURL=index.js.map
